import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';

import { Injectable } from '@angular/core';

import { ApDateService } from '@ap/date';

import { GroupedMessagesByTimestamp } from '../../conversational-connector/klant-conversational-connector.model';

@Injectable({ providedIn: 'root' })
export class DateGroupService {
    private chatMessagesCache: ChatMessageModel[] = [];
    private groupedMessagesCache: GroupedMessagesByTimestamp[] = [];

    /**
     * Groups chat messages by their date.
     * Optimized to append new messages or patch a single message instead of rebuilding everything.
     */
    public groupChatMessageByDate(messages: ChatMessageModel[]): GroupedMessagesByTimestamp[] {
        if (messages === this.chatMessagesCache) {
            return this.groupedMessagesCache;
        }

        if (this.onlyNewMessagesAdded(messages)) {
            const newMessages = messages.slice(this.chatMessagesCache.length);
            this.groupedMessagesCache = this.addNewMessages(this.groupedMessagesCache, newMessages);
        } else {
            const singleMessageChangedIndex = this.findSingleChangedIndex(messages);
            const onlyOneMessageChanged = singleMessageChangedIndex !== -1;

            if (onlyOneMessageChanged) {
                this.groupedMessagesCache = this.applySingleMessagePatch(
                    this.groupedMessagesCache,
                    messages[singleMessageChangedIndex],
                    this.chatMessagesCache[singleMessageChangedIndex]
                );
            } else {
                this.groupedMessagesCache = this.buildFromScratch(messages);
            }
        }

        this.chatMessagesCache = messages;
        return this.groupedMessagesCache;
    }

    private onlyNewMessagesAdded(next: ChatMessageModel[]): boolean {
        const prev = this.chatMessagesCache;
        if (prev.length === 0 || next.length <= prev.length) {
            return false;
        }
        for (let i = 0; i < prev.length; i++) {
            if (next[i] !== prev[i]) {
                return false;
            }
        }
        return true;
    }

    private findSingleChangedIndex(next: ChatMessageModel[]): number {
        const prev = this.chatMessagesCache;
        if (prev.length === 0 || next.length !== prev.length) {
            return -1;
        }

        let diff = -1;
        for (let i = 0; i < next.length; i++) {
            if (next[i] !== prev[i]) {
                if (diff !== -1) {
                    return -1;
                }
                diff = i;
            }
        }
        return diff;
    }

    private addNewMessages(groups: GroupedMessagesByTimestamp[], newMessages: ChatMessageModel[]): GroupedMessagesByTimestamp[] {
        const result = [...groups];
        for (const message of newMessages) {
            this.insertMessageInGroup(result, message);
        }
        return result;
    }

    private applySingleMessagePatch(
        groups: GroupedMessagesByTimestamp[],
        newMessage: ChatMessageModel,
        oldMessage: ChatMessageModel
    ): GroupedMessagesByTimestamp[] {
        const result = [...groups];
        this.removeMessageFromGroup(result, oldMessage);
        this.insertMessageInGroup(result, newMessage);
        return result;
    }

    private insertMessageInGroup(groups: GroupedMessagesByTimestamp[], message: ChatMessageModel): void {
        const key = this.getDateKey(message);
        const group = this.findGroupByDateKey(groups, key);

        if (group) {
            group.chatMessages = [...group.chatMessages, message];
        } else {
            groups.push({
                date: ApDateService.utc(new Date(key)),
                chatMessages: [message]
            });
        }
    }

    private removeMessageFromGroup(groups: GroupedMessagesByTimestamp[], message: ChatMessageModel): void {
        const key = this.getDateKey(message);
        const index = groups.findIndex((g) => this.dateToKey(g.date) === key);

        const group = groups[index];
        group.chatMessages = group.chatMessages.filter((m) => m !== message);
        if (group.chatMessages.length === 0) {
            groups.splice(index, 1);
        }
    }

    private buildFromScratch(messages: ChatMessageModel[]): GroupedMessagesByTimestamp[] {
        if (!messages.length) {
            return [];
        }

        const map = new Map<string, ChatMessageModel[]>();
        for (const message of messages) {
            const key = this.getDateKey(message);
            const bucket = map.get(key);
            if (bucket) {
                bucket.push(message);
            } else {
                map.set(key, [message]);
            }
        }

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, msgs]) => ({
                date: ApDateService.utc(new Date(key)),
                chatMessages: msgs
            }));
    }

    private getDateKey(chatMessage: ChatMessageModel): string {
        return new Date(chatMessage.occurredAt / 1000).toISOString().slice(0, 10);
    }

    private dateToKey(date: Date): string {
        return date.toISOString().slice(0, 10);
    }

    private findGroupByDateKey(groups: GroupedMessagesByTimestamp[], dateKey: string): GroupedMessagesByTimestamp | undefined {
        return groups.find((group) => this.dateToKey(group.date) === dateKey);
    }
}
